import { useState, useEffect } from 'react';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';

/**
 * We have values placed in a browser's local storage that should be used to
 * populate certain UI states for new templates. However if it is not a new template,
 * those states should instead be set using the template's values,
 * and during any change to the values,
 * the local storage should be updated to reflect those values.
 *
 * This is a custom hook that keeps the local storage in-sync with updates to template state.
 */
export const useLocalStorageOrUseState = <StoredValueType>(
  key: string,
  initialValue: StoredValueType,
  templateValue: StoredValueType,
  isNewTemplate: boolean,
): [StoredValueType, (arg: StoredValueType) => void] => {
  const [localStorageValue, setLocalStorageValue] = useLocalStorage<StoredValueType>(
    key,
    initialValue,
  );

  const [stateValue, setStateValue] = useState<StoredValueType>(
    isNewTemplate ? localStorageValue : templateValue,
  );

  useEffect(() => {
    setLocalStorageValue(templateValue);
  }, [templateValue]);

  return [stateValue, setStateValue];
};
